import React from "react";
import ReactDOM from "react-dom/client";
import { ErrorBoundary } from "./components/molecules/ErrorBoundary";
import { ThemeProvider } from "./context/ThemeContext";
import { DensityProvider } from "./context/DensityContext";
import App from "./App.jsx";
import "./index.css";
// Canonical design-system token files — imported after index.css so they win
// on any conflicting variable names (e.g. --brand-gold canonical value).
import "./design-system/tokens/color.css";
import "./design-system/tokens/type.css";
import "./design-system/tokens/space.css";
import "./design-system/tokens/motion.css";
import "./design-system/tokens/status.css";
import "./i18n";
import { reportFrontendError } from "./utils/errorReporter";
import { resumeStoredSession } from "./lib/api";

// Global JS error handler — catches unhandled exceptions
window.onerror = (message, source, lineno, colno, error) => {
  reportFrontendError({
    type: "js_error",
    message: String(message),
    stack: error?.stack,
    source: String(source ?? ""),
    lineno: lineno ?? undefined,
    colno: colno ?? undefined,
    url: window.location.href,
  });
};

// Global promise rejection handler — catches unhandled async errors
window.onunhandledrejection = (event) => {
  reportFrontendError({
    type: "promise_rejection",
    message: String(event.reason),
    stack: event.reason?.stack,
    url: window.location.href,
  });
};

function renderApp() {
  ReactDOM.createRoot(document.getElementById("root")).render(
    <React.StrictMode>
      <ErrorBoundary>
        <ThemeProvider>
          <DensityProvider>
            <App />
          </DensityProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </React.StrictMode>
  );
}

// On native (Capacitor), an app update can wipe WKWebView localStorage, logging
// the user out + re-triggering 2FA. Restore the auth token + trusted-device
// token from durable Preferences BEFORE the first render so RequireAuth sees a
// valid session and there's no login flash. Web renders immediately (no delay).
const _cap = (typeof window !== "undefined" ? window.Capacitor : undefined);
if (_cap && typeof _cap.isNativePlatform === "function" && _cap.isNativePlatform()) {
  // Fire-and-forget, NOT awaited: per @capgo/capacitor-updater's own docs,
  // notifyAppReady() must fire as early as possible and must never be
  // gated behind other async work (network calls, auth restore) — doing so
  // risks missing the plugin's rollback timeout (MOBILE-OTA-02, #1091).
  import("./utils/liveUpdate").then(m => m.notifyAppReady()).catch(() => {});
  import("./lib/native")
    .then(m => m.restoreAuthFromPreferences())
    .catch(() => {})
    .then(() => resumeStoredSession())
    .catch(() => false)
    .finally(renderApp);
} else {
  resumeStoredSession().catch(() => false).finally(renderApp);
}
// cache bust Tue Mar 17 16:15:13 UTC 2026
